import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import moment from "moment/moment";
import { Avatar, Spin } from "antd";
import ColumnBarChart from "../../components/chart/ColumnBarChart";
import { GetAdminDashboardApi } from "../../api/request/dashboard/dashboard";
import SkeletonTable from "../../components/table/SkeletonTable";
import recentFilesColumn from "../../columns/dashboard/recentFiles";
import recentUserColumn from "../../columns/dashboard/recentUser";
import servicesColumn from "../../columns/dashboard/services";
import UserAvatar from "../../components/common/UserAvatar";
import { getInitials } from "../../utils/utils";
import DonutChart from "../../components/chart/DonutChart";
import ProcessBar from "../../components/chart/ProcessBar";
import { useSelector } from "react-redux";

const Dashboard = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [taskData, setTaskData] = useState({
    categories: [],
    series: [],
  });
  const [recentFiles, setRecentFiles] = useState([]);
  const [serviceData, setServiceData] = useState([]);
  const [latestUsers, setLatestUsers] = useState([]);
  const userDetails = useSelector((state) => state?.user?.userDetails);

  const getDashboard = async () => {
    try {
      setIsLoading(true);
      const { data } = await GetAdminDashboardApi();
      if (data?.status) {
        setData(data?.data);
        const chartData = data.data.chart_data || [];
        const taskData = {
          categories: Array.isArray(chartData)
            ? chartData.map((item) => moment(item.date).format("MMM DD"))
            : [],
          series: [
            {
              name: "Closed",
              data: Array.isArray(chartData)
                ? chartData.map((item) => item.completed_tasks || 0)
                : [],
            },
            {
              name: "In-Process",
              data: Array.isArray(chartData)
                ? chartData.map((item) => item.running_tasks || 0)
                : [],
            },
          ],
        };
        setTaskData(taskData);
        setRecentFiles(
          data?.data?.latest_submitted_file?.map((item) => {
            return {
              ...item,
              task: item?.running_task?.pipeline_manage_task,
              userDetails: <UserAvatar data={item?.user} />,
              created_at: item?.created_at
                ? moment(item?.created_at).format("lll")
                : "--",
            };
          })
        );
        setServiceData(
          data?.data?.services_count.map((item) => {
            return {
              ...item,
              service_name: (
                <div className="flex items-center gap-2">
                  <span className=" flex items-center justify-center rounded-full">
                    <img
                      src="/assets/icons/services.svg"
                      alt=""
                      className="h-4 w-4"
                    />
                  </span>
                  <span
                    className="capitalize w-[150px] truncate"
                    title={item?.service_name}
                  >
                    {item?.service_name}
                  </span>
                </div>
              ),
            };
          })
        );
        setLatestUsers(
          data?.data?.latest_users?.map((item) => {
            return {
              ...item,
              userDetails: <UserAvatar data={item} />,
              created_at: item?.created_at
                ? moment(item?.created_at).format("lll")
                : "--",
            };
          })
        );
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getDashboard();
  }, []);

  const DashboardCard = ({
    title,
    mainValue,
    subStats,
    icon,
    bgColor,
    redirectPath,
  }) => (
    <div
      className="p-4 bg-white border rounded-xl shadow hover:shadow-md transition flex flex-col cursor-pointer"
      onClick={() => navigate(redirectPath)}
    >
      <div className="flex justify-between">
        <p className="text-[18px] font-bold">{title}</p>
        {icon && (
          <div
            className={`${bgColor} rounded-lg size-10 flex items-center justify-center`}
          >
            <img src={icon} alt="icon" className="w-6 h-6" />
          </div>
        )}
      </div>

      {subStats ? (
        <div className="flex justify-between mt-3">
          {subStats?.map((stat, idx) => (
            <div key={idx} className="flex flex-col items-center">
              <p className="text-xl font-semibold text-gray-800">
                {stat?.value}
              </p>
              <p className="text-xs font-medium">{stat?.label}</p>
            </div>
          ))}
        </div>
      ) : (
        <p className="card-count mt-3">{mainValue}</p>
      )}
    </div>
  );

  const cards = [
    {
      title: "Total Clients",
      mainValue: data?.total_clients || 0,
      icon: "/assets/icons/clients.svg",
      bgColor: "bg-[#cfcced]",
      redirectPath: "/clients",
    },
    {
      title: "Total Files",
      mainValue: data?.total_file_counts?.total_files || 0,
      icon: "/assets/icons/files.svg",
      bgColor: "bg-[#cdd8ec]",
      redirectPath: "/files_report",
    },
    {
      title: "Invoices",
      icon: "/assets/icons/invoice-files.svg",
      bgColor: "bg-[#DDF6E8]",
      redirectPath: "/invoice_report",
      subStats: [
        { label: "Paid", value: data?.paid_invoice || 0 },
        { label: "Unpaid", value: data?.unpaid_invoice || 0 },
        { label: "Total", value: data?.total_invoice || 0 },
      ],
    },
    {
      title: "Signatures",
      icon: "/assets/icons/signature-dashboard.svg",
      bgColor: "bg-[#E5FAFF]",
      redirectPath: "/signature_report",
      subStats: [
        { label: "Pending", value: data?.pending_signature_count || 0 },
        { label: "Completed", value: data?.completed_signature_count || 0 },
        { label: "Total", value: data?.total_signature_count || 0 },
      ],
    },
    {
      title: "Interac e-Transfers",
      icon: "/assets/icons/money-transfer.svg",
      bgColor: "bg-[#FEF3C7]",
      redirectPath: "/payments_report",
      subStats: [
        {
          label: "Pending",
          value: data?.unpaid_interac_e_transfer_payments || 0,
        },
        {
          label: "Completed",
          value: data?.paid_interac_e_transfer_payments || 0,
        },
        { label: "Total", value: data?.total_interac_e_transfer_payments || 0 },
      ],
    },
  ];

  const lineData = [
    {
      name: "Total Received",
      count: data?.total_received_amount || 0,
      bgColor: "bg-[#E5F1F2]",
      color: "#28C76F",
    },
    {
      name: "Total Pending",
      count: data?.total_pending_amount || 0,
      bgColor: "bg-[#FFE2E3]",
      color: "#FF5964",
    },
  ];

  const formatActivityDate = (date) => {
    return moment(date)
      .fromNow()
      .replace("second", "sec")
      .replace("minute", "min");
  };

  const handleFileView = (data) => {
    navigate(`/file/${data?.service_type_id}/${data?.id}`, {
      state: { client: data?.user, task: data?.task },
    });
  };

  const handleClientView = (data) => {
    navigate(`/client_details/${data?.id}`, {
      state: { client: data?.user, task: data?.task },
    });
  };
  const colors = [
    { color: "#00727D", bgColor: "#E5F1F2" }, //theme
    { color: "#7367F0", bgColor: "#E9E7FD" }, //purple
    { color: "#00BAD1", bgColor: "#D6F4F8" }, //blue
    { color: "#FF9F43", bgColor: "#FFF0E1" }, // yellow
    { color: "#28C76F", bgColor: "#DDF6E8" }, // green
    { color: "#FF4C51", bgColor: "#FFE2E3" }, // red
  ];

  const randomColor = useMemo(() => {
    if (!data?.profile_image) {
      const index = Math.floor(Math.random() * colors.length);
      return colors[index];
    }
    return null;
  }, [data?.profile_image]);
  if (!data) return null;

  return (
    <>
      {isLoading ? (
        <div className="flex justify-center flex-col mt-[20%]">
          <Spin size="large" />
          <p className="primary text-center mt-2">Loading...</p>
        </div>
      ) : (
        <div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
            {cards?.map((card, idx) => (
              <DashboardCard key={idx} {...card} />
            ))}
          </div>

          <div className="flex flex-col xl:flex-row gap-5 sm:mt-5 mt-4">
            {/* Left Section (Services + File Status) */}
            <div className="space-y-5 w-full xl:w-1/2">
              {/* Services Table */}
              <div className="bg-white border rounded-xl p-4 shadow-md hover:shadow-lg transition-all relative h-[275px] overflow-hidden">
                <div
                  className="absolute top-0 left-0 w-full h-1 
  bg-gradient-to-r from-blue-500 via-green-500 to-purple-500 
  rounded-t-full bg-[length:200%_200%] animate-gradient-x"
                ></div>

                <div className="flex items-center justify-between sub-title mb-4">
                  <h3 className="title font-semibold text-lg">Services</h3>
                </div>

                <SkeletonTable
                  columns={servicesColumn}
                  data={serviceData}
                  isLoading={isLoading}
                  index={false}
                  stickHeader={true}
                  scrollY={150}
                  onRowClick={() => navigate("/services")}
                />
              </div>

              {/* File Status Overview */}
              <div className="border rounded-xl bg-white flex flex-col overflow-x-auto">
                <div className="flex items-center justify-between w-full px-4 py-2">
                  <p className="title">File Status Overview</p>
                  <div className="flex items-center justify-end gap-3 text-sm mb-3">
                    <p>
                      <span className="text-xl text-secondary">●</span> Closed
                    </p>
                    <p>
                      <span className="text-xl text-Gray ">●</span> In-Process
                    </p>
                  </div>
                </div>

                <div className="w-full">
                  <ColumnBarChart
                    categories={taskData.categories}
                    series={taskData.series}
                  />
                </div>
              </div>
            </div>

            {/* Middle Section (Files + Payment Summary) */}
            <div className="flex flex-col gap-5 w-full xl:w-1/4">
              {/* Files Donut Chart */}
              <div className="h-full flex flex-col bg-white border rounded-xl p-4">
                <h2 className="title">Files</h2>
                <div className="flex-1 flex items-center justify-center">
                  <DonutChart
                    role={userDetails?.role}
                    fileCounts={data?.file_counts_donut_chart}
                  />
                </div>
              </div>

              {/* Payment Summary (only for admin) */}
              {userDetails?.role === "admin" && (
                <div className="bg-white border rounded-xl p-4 h-full flex flex-col justify-between">
                  <h2 className="title">Payment Summary</h2>
                  <div className="mb-4">
                    <div className="flex justify-between gap-2 mb-1">
                      <div>
                        <p className="mb-6 text-xl font-semibold xl:text-base">
                          Total Received
                        </p>
                        <p className="text-lg font-bold">
                          ${data?.total_received_amount}
                        </p>
                      </div>
                      <div className="flex flex-col items-end">
                        <p className="mb-6 text-xl font-semibold xl:text-base">
                          Total Pending
                        </p>
                        <p className="text-lg font-bold w-fit">
                          ${data?.total_pending_amount}
                        </p>
                      </div>
                    </div>
                    <ProcessBar data={lineData} />
                  </div>
                </div>
              )}
            </div>

            {/* Right Section (Latest Clients) */}
            <div className="bg-white border rounded-xl p-4 overflow-x-auto w-full xl:w-1/3">
              <div className="flex items-center justify-between sub-title mb-4">
                <h3 className="title">Latest Clients</h3>
                <Link to="/clients" className="text-blue-500">
                  View All
                </Link>
              </div>
              <div className="no-scrollbar">
                <SkeletonTable
                  columns={recentUserColumn}
                  data={latestUsers}
                  isLoading={isLoading}
                  onRowClick={(data) => handleClientView(data)}
                  index={false}
                  stickHeader={true}
                  scrollY={450}
                  infiniteScroll={latestUsers?.length > 6}
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 mt-5">
            <div className="md:col-span-2 xl:col-span-1 bg-white border rounded-xl p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="title">Recent Activity</h3>
                <Link to="/activity" className="text-blue-500">
                  View All
                </Link>
              </div>

              <div className="h-[400px] overflow-y-scroll scrollbar-hide">
                <div className="sm:space-y-5 space-y-4">
                  {data?.latest_acitivty?.map((item) => (
                    <div className="flex items-center gap-3">
                      <div>
                        <Avatar
                          size="large"
                          style={{
                            color: !data?.profile_image
                              ? randomColor?.color
                              : "",
                            backgroundColor: !data?.profile_image
                              ? randomColor?.bgColor
                              : "",
                          }}
                          src={item?.user?.profile_image}
                        >
                          {getInitials(item?.user?.name)}
                        </Avatar>
                      </div>

                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="sub-title capitalize">
                            {item?.user?.name}
                          </p>
                          <p className="text-xs text-Gray text-right">
                            {formatActivityDate(item?.created_at)}
                          </p>
                        </div>

                        <p className="text-Gray text-wrap w-full sm:max-w-full max-w-60">
                          {item?.activity_message}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="md:col-span-2 xl:col-span-3 bg-white border rounded-xl p-4 overflow-x-auto">
              <div className="flex items-center justify-between sub-title mb-4">
                <h3 className="title">Recent File</h3>
                <Link to="/services" className="text-blue-500">
                  View All
                </Link>
              </div>

              <SkeletonTable
                columns={recentFilesColumn}
                data={recentFiles}
                isLoading={isLoading}
                onRowClick={(data) => handleFileView(data)}
                index={false}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Dashboard;
